/* tt-card-gyro.js — mobile gyroscope tilt + holographic sheen for the
 * "Find your game" cards.
 *
 * The cards already react to four CSS custom properties that the desktop
 * mouse-tilt feeds (sections/featured-games.liquid): --tt-rx / --tt-ry (the 3D
 * tilt) and --tt-mx / --tt-my (the sheen position). On a phone there is no
 * cursor, so this drives the same variables from the DeviceOrientation sensor.
 *
 * The sheen is treated as ONE wide highlight moving across the whole section:
 * a single soft light sweeps the grid as the phone tilts, and each card renders
 * the slice of that sweep falling over it — so the foil reads as one broad
 * holographic sheet, not a spotlight per tile.
 *
 * A branded pill toggles motion on/off. iOS 13+ needs a one-tap permission
 * gesture before any motion data is delivered (handled on the first tap);
 * Android (over HTTPS) just works.
 */
(function () {
  var grid = document.querySelector('[data-tt-holo-grid]');
  if (!grid) return;

  var mq = window.matchMedia;
  var coarse = mq && mq('(hover: none) and (pointer: coarse)').matches;
  var reduce = mq && mq('(prefers-reduced-motion: reduce)').matches;
  if (!coarse || reduce || !window.DeviceOrientationEvent) return;

  var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-tt-holo]'));
  if (!cards.length) return;

  // --- Tuning ---------------------------------------------------------------
  var MAX_DEG = 12; // hard cap on card tilt
  var TILT_SCALE = 0.55; // card-degrees per phone-degree
  var SWEEP_SCALE = 2.4; // highlight travel across the grid (%) per phone-degree
  var HUE_SCALE = 0.9; // foil hue-shift (deg) per phone-degree
  var MAX_HUE = 60;
  var EASE = 0.16; // tilt smoothing (stays responsive)
  var SHEEN_EASE = 0.055; // sheen smoothing — low = slow, gradual drift

  var base = null;
  var target = { rx: 0, ry: 0, gx: 50, gy: 50, hue: 0 };
  var cur = { rx: 0, ry: 0, gx: 50, gy: 50, hue: 0 };
  var running = false; // rAF loop active
  var live = false; // motion enabled

  // Per-card geometry within the grid (centre + size as % of the grid box),
  // so the one grid-space highlight maps into each card's local --tt-mx/--tt-my.
  var metrics = [];
  function measure() {
    var g = grid.getBoundingClientRect();
    if (!g.width || !g.height) return;
    metrics = cards.map(function (card) {
      var r = card.getBoundingClientRect();
      return {
        cx: ((r.left + r.width / 2) - g.left) / g.width * 100,
        cy: ((r.top + r.height / 2) - g.top) / g.height * 100,
        w: (r.width / g.width) * 100,
        h: (r.height / g.height) * 100
      };
    });
  }

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  function onOrient(e) {
    if (e.beta == null && e.gamma == null) return;
    var beta = e.beta || 0;
    var gamma = e.gamma || 0;
    if (!base) base = { beta: beta, gamma: gamma };
    var dBeta = beta - base.beta;
    var dGamma = gamma - base.gamma;
    target.ry = clamp(dGamma * TILT_SCALE, -MAX_DEG, MAX_DEG);
    target.rx = clamp(-dBeta * TILT_SCALE, -MAX_DEG, MAX_DEG);
    target.gx = clamp(50 + dGamma * SWEEP_SCALE, -40, 140);
    target.gy = clamp(50 + dBeta * SWEEP_SCALE, -40, 140);
    target.hue = clamp((dGamma + dBeta * 0.6) * HUE_SCALE, -MAX_HUE, MAX_HUE);
    if (!running) {
      running = true;
      requestAnimationFrame(tick);
    }
  }

  function tick() {
    // If motion was turned off, bail before re-applying any transform so the
    // reset in hideFoil() is the final word (avoids a queued frame snapping the
    // cards back to a tilt).
    if (!live) {
      running = false;
      return;
    }
    cur.rx += (target.rx - cur.rx) * EASE;
    cur.ry += (target.ry - cur.ry) * EASE;
    cur.gx += (target.gx - cur.gx) * SHEEN_EASE;
    cur.gy += (target.gy - cur.gy) * SHEEN_EASE;
    cur.hue += (target.hue - cur.hue) * SHEEN_EASE;

    for (var i = 0; i < cards.length; i++) {
      var s = cards[i].style;
      var m = metrics[i];
      var mx = 50;
      var my = 50;
      if (m && m.w && m.h) {
        mx = 50 + ((cur.gx - m.cx) / m.w) * 100;
        my = 50 + ((cur.gy - m.cy) / m.h) * 100;
      }
      s.setProperty('--tt-rx', cur.rx.toFixed(2) + 'deg');
      s.setProperty('--tt-ry', cur.ry.toFixed(2) + 'deg');
      s.setProperty('--tt-mx', mx.toFixed(1) + '%');
      s.setProperty('--tt-my', my.toFixed(1) + '%');
      s.setProperty('--tt-hue', cur.hue.toFixed(1) + 'deg');
    }

    var settled =
      Math.abs(target.rx - cur.rx) < 0.05 &&
      Math.abs(target.ry - cur.ry) < 0.05 &&
      Math.abs(target.gx - cur.gx) < 0.1 &&
      Math.abs(target.gy - cur.gy) < 0.1 &&
      Math.abs(target.hue - cur.hue) < 0.2;
    running = !settled && live;
    if (running) requestAnimationFrame(tick);
  }

  // Reveal a WIDE, soft holographic sheen (set inline so it shows regardless of
  // any cached stylesheet). A broad soft glow + low-opacity rainbow wash that
  // sweeps with the highlight — not a tight spotlight.
  function showFoil() {
    measure();
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      card.style.transition = 'transform 120ms linear';
      card.classList.add('is-gyro');

      var img = card.querySelector('.tt-holo-card__image img');
      if (img) {
        img.style.opacity = '0.82';
        img.style.filter = 'saturate(1.08)';
      }
      var shine = card.querySelector('.tt-holo-card__shine');
      if (shine) {
        shine.style.opacity = '0.6';
        shine.style.mixBlendMode = 'screen';
        shine.style.filter = 'blur(14px) saturate(1.55) hue-rotate(var(--tt-hue, 0deg))';
      }
      // Broad, diffuse wash (oversized + very soft, so there's no "ball" edge —
      // just a gentle gradient of light across the whole card that drifts slowly).
      var glare = card.querySelector('.tt-holo-card__glare');
      if (glare) {
        glare.style.opacity = '0.85';
        glare.style.mixBlendMode = 'screen';
        glare.style.background =
          'radial-gradient(180% 160% at var(--tt-mx) var(--tt-my),' +
          ' rgba(255,255,255,0.26), rgba(255,255,255,0.08) 46%, transparent 82%)';
      }
    }
  }

  function hideFoil() {
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      card.classList.remove('is-gyro');
      var st = card.style;
      st.transition = 'transform 400ms cubic-bezier(0.2, 0.8, 0.2, 1)';
      st.setProperty('--tt-rx', '0deg');
      st.setProperty('--tt-ry', '0deg');
      st.setProperty('--tt-mx', '50%');
      st.setProperty('--tt-my', '50%');
      st.setProperty('--tt-hue', '0deg');
      var img = card.querySelector('.tt-holo-card__image img');
      if (img) {
        img.style.opacity = '';
        img.style.filter = '';
      }
      var shine = card.querySelector('.tt-holo-card__shine');
      if (shine) {
        shine.style.opacity = '';
        shine.style.mixBlendMode = '';
        shine.style.filter = '';
      }
      var glare = card.querySelector('.tt-holo-card__glare');
      if (glare) {
        glare.style.opacity = '';
        glare.style.mixBlendMode = '';
        glare.style.background = '';
      }
    }
  }

  // --- Enable / disable -----------------------------------------------------
  function startMotion() {
    live = true;
    base = null; // recalibrate to the current resting angle
    cur = { rx: 0, ry: 0, gx: 50, gy: 50, hue: 0 };
    target = { rx: 0, ry: 0, gx: 50, gy: 50, hue: 0 };
    showFoil();
    window.addEventListener('deviceorientation', onOrient, true);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', onRotate);
    setPill(true);
  }

  function stopMotion() {
    live = false;
    window.removeEventListener('deviceorientation', onOrient, true);
    window.removeEventListener('resize', measure);
    window.removeEventListener('orientationchange', onRotate);
    hideFoil();
    setPill(false);
  }

  function onRotate() {
    setTimeout(measure, 300);
  }

  var needsPermission = typeof DeviceOrientationEvent.requestPermission === 'function';

  function enable() {
    if (!needsPermission) {
      startMotion();
      return;
    }
    DeviceOrientationEvent.requestPermission()
      .then(function (state) {
        if (state === 'granted') startMotion();
      })
      .catch(function () {});
  }

  // --- Pill toggle ----------------------------------------------------------
  var pill = document.createElement('button');
  pill.type = 'button';
  pill.className = 'tt-holo-gyro-prompt';

  function setPill(on) {
    pill.classList.toggle('is-on', on);
    pill.setAttribute('aria-pressed', on ? 'true' : 'false');
    pill.setAttribute(
      'aria-label',
      on ? 'Tap to turn off card motion' : 'Tap to enable card motion'
    );
    pill.innerHTML =
      '<span class="tt-holo-gyro-prompt__dot" aria-hidden="true"></span>' +
      '<span class="tt-holo-gyro-prompt__label">' +
      (on ? 'Tap to turn off' : 'Tap to enable card motion') +
      '</span>';
  }

  setPill(false);
  pill.addEventListener('click', function () {
    if (live) {
      stopMotion();
    } else {
      enable();
    }
  });
  grid.parentNode.insertBefore(pill, grid.nextSibling);
})();
